import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, FileText, Download, Eye } from 'lucide-react';

// TaxSetu Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_7a5b9feb-7efd-48fe-91ba-ada6a1b3fe62/artifacts/dbzfuj5b_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

/**
 * File Returns Page - Period Selection and Return Type Cards
 * Flow: Select FY/Quarter/Period → Search → Show GSTR-1, GSTR-2B, GSTR-3B cards
 */
const FileReturnsPage = ({ companyData, onSelectReturn, formType = 'GSTR-1' }) => {
  // Compute default filing period based on current date
  // GST returns are filed for the PREVIOUS month
  const computeDefaults = () => {
    const now = new Date();
    // Filing period = previous month
    const filingDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const filingMonth = filingDate.getMonth(); // 0-11
    const filingYear = filingDate.getFullYear();

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const periodName = monthNames[filingMonth];

    // GST Financial Year: April-March. If month >= April (3), FY = year-year+1, else FY = year-1 to year
    let fyStart, fyEnd;
    if (filingMonth >= 3) { // Apr (3) onwards
      fyStart = filingYear;
      fyEnd = filingYear + 1;
    } else { // Jan-Mar
      fyStart = filingYear - 1;
      fyEnd = filingYear;
    }
    const fy = `${fyStart}-${fyEnd.toString().slice(-2)}`;

    // Quarter mapping (GST FY based: Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar)
    let qtr;
    if (filingMonth >= 3 && filingMonth <= 5) qtr = 'Q1';
    else if (filingMonth >= 6 && filingMonth <= 8) qtr = 'Q2';
    else if (filingMonth >= 9 && filingMonth <= 11) qtr = 'Q3';
    else qtr = 'Q4';

    return { fy, qtr, period: periodName };
  };

  const defaults = computeDefaults();
  const [financialYear, setFinancialYear] = useState(defaults.fy);
  const [quarter, setQuarter] = useState(defaults.qtr);
  const [period, setPeriod] = useState(defaults.period);
  const [showReturns, setShowReturns] = useState(false);

  const currentDate = new Date();

  // Build dynamic list of last 4 financial years (latest first)
  const buildFYList = () => {
    const list = [];
    const startYear = parseInt(defaults.fy.split('-')[0]);
    for (let i = 0; i < 4; i++) {
      const s = startYear - i;
      const e = (s + 1).toString().slice(-2);
      list.push(`${s}-${e}`);
    }
    return list;
  };
  const financialYears = buildFYList();
  
  // Quarters with months
  const quarters = {
    'Q1': { label: 'Quarter 1 (Apr - Jun)', months: ['April', 'May', 'June'] },
    'Q2': { label: 'Quarter 2 (Jul - Sep)', months: ['July', 'August', 'September'] },
    'Q3': { label: 'Quarter 3 (Oct - Dec)', months: ['October', 'November', 'December'] },
    'Q4': { label: 'Quarter 4 (Jan - Mar)', months: ['January', 'February', 'March'] }
  };

  // Get months for selected quarter
  const getMonthsForQuarter = () => {
    return quarters[quarter]?.months || [];
  };

  // Calculate due dates
  const getDueDate = (returnType) => {
    const monthIndex = getMonthsForQuarter().indexOf(period);
    const quarterMonths = { 'Q1': 3, 'Q2': 6, 'Q3': 9, 'Q4': 0 };
    const baseMonth = quarterMonths[quarter] + monthIndex;
    
    // GSTR-1 due: 11th of next month, GSTR-3B due: 20th of next month
    const dueDay = returnType === 'GSTR1' ? 11 : 20;
    const year = quarter === 'Q4' ? parseInt(financialYear.split('-')[0]) + 1 : parseInt(financialYear.split('-')[0]);
    const dueMonth = baseMonth + 1;
    
    return `${dueDay.toString().padStart(2, '0')}/${(dueMonth + 1).toString().padStart(2, '0')}/${year}`;
  };

  const handleSearch = () => {
    setShowReturns(true);
  };

  const handlePrepareOnline = (returnType) => {
    onSelectReturn(returnType, { financialYear, quarter, period });
  };

  return (
    <div className="min-h-screen bg-[#e8f4f8] flex flex-col">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={TAXSETU_LOGO} alt="TaxSetu" className="h-10" />
            <div className="border-l border-white/30 pl-4">
              <h1 className="text-xl font-normal">Demo Goods and Services Tax</h1>
              <p className="text-sm text-gray-300">Practical Simulation - Educational Purpose Only</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="text-white">{companyData.legalName?.slice(0, 20)}...</p>
            <p className="text-gray-300 font-mono text-xs">{companyData.gstin}</p>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-[#2a5298] py-2">
        <div className="max-w-7xl mx-auto flex gap-1 px-6">
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Dashboard</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Services ▾</button>
          <button className="px-4 py-2 text-yellow-300 text-sm">GST Law</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Downloads ▾</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Search Taxpayer ▾</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">Help and Taxpayer Facilities</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">e-Invoice</button>
          <button className="px-4 py-2 text-white hover:bg-white/10 text-sm">News and Updates</button>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b py-2 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-blue-600 cursor-pointer hover:underline">Dashboard</span>
            <span className="text-gray-400">›</span>
            <span className="text-gray-600">Returns</span>
          </div>
          <span className="text-blue-600">🌐 English</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          {/* File Returns Card */}
          <div className="bg-white rounded shadow-sm mb-6">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold text-gray-800">File Returns</h2>
            </div>
            
            <div className="p-6">
              {/* Info Banner */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  Nil return for GSTR-1, GSTR-3B and CMP-08 can now be filed through SMS.
                </p>
              </div>

              {/* Mandatory Fields Note */}
              <p className="text-right text-sm mb-4">
                <span className="text-red-500">•</span> Indicates Mandatory Fields
              </p>

              {/* Period Selection */}
              <div className="flex flex-wrap items-end gap-6 mb-6">
                {/* Financial Year */}
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Financial Year<span className="text-red-500">*</span>
                  </label>
                  <Select value={financialYear} onValueChange={setFinancialYear}>
                    <SelectTrigger className="w-full border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {financialYears.map(fy => (
                        <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Quarter */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quarter<span className="text-red-500">*</span>
                  </label>
                  <Select value={quarter} onValueChange={(v) => { setQuarter(v); setPeriod(quarters[v].months[0]); }}>
                    <SelectTrigger className="w-full border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(quarters).map(([key, q]) => (
                        <SelectItem key={key} value={key}>{q.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Period (Month) */}
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Period<span className="text-red-500">*</span>
                  </label>
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-full border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getMonthsForQuarter().map(month => (
                        <SelectItem key={month} value={month}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Button */}
                <Button 
                  onClick={handleSearch}
                  className="bg-[#1e3a5f] hover:bg-[#2a5298] text-white px-8"
                  data-testid="file-returns-search-btn"
                >
                  SEARCH
                </Button>
              </div>
            </div>
          </div>

          {/* Return Type Cards - Show after Search */}
          {showReturns && (
            <>
              {/* Info Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6 flex items-start gap-2">
                <span className="text-blue-600 font-bold">ℹ</span>
                <p className="text-sm text-gray-700">
                  You have selected to file the return on monthly frequency, GSTR-1 and GSTR-3B shall be required to be filed for each month of the quarter.
                </p>
                <button className="ml-auto text-gray-400 hover:text-gray-600">✕</button>
              </div>

              {/* Return Cards Grid */}
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {/* GSTR-1 Card */}
                <div className="bg-white rounded shadow-sm overflow-hidden">
                  <div className="bg-[#1e3a5f] text-white p-4 text-center">
                    <h3 className="font-semibold">Details of outward supplies of goods or services</h3>
                    <p className="text-lg font-bold mt-1">GSTR1</p>
                  </div>
                  <div className="p-4">
                    <p className="text-center mb-4">
                      <span className="text-gray-600">Due Date - </span>
                      <span className="text-red-500 font-semibold">{getDueDate('GSTR1')}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handlePrepareOnline('GSTR1')}
                        className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm"
                      >
                        PREPARE ONLINE
                      </Button>
                      <Button variant="outline" className="flex-1 border-[#1e3a5f] text-[#1e3a5f] text-sm">
                        PREPARE OFFLINE
                      </Button>
                    </div>
                  </div>
                </div>

                {/* GSTR-2B Card */}
                <div className="bg-white rounded shadow-sm overflow-hidden">
                  <div className="bg-[#1e3a5f] text-white p-4 text-center">
                    <h3 className="font-semibold">Auto - drafted ITC Statement for the month</h3>
                    <p className="text-lg font-bold mt-1">GSTR2B</p>
                  </div>
                  <div className="p-4">
                    <p className="text-center mb-4 text-gray-400">View Only</p>
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm">
                        VIEW
                      </Button>
                      <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm">
                        DOWNLOAD
                      </Button>
                    </div>
                  </div>
                </div>

                {/* GSTR-3B Card */}
                <div className="bg-white rounded shadow-sm overflow-hidden">
                  <div className="bg-[#1e3a5f] text-white p-4 text-center">
                    <h3 className="font-semibold">Monthly Return</h3>
                    <p className="text-lg font-bold mt-1">GSTR-3B</p>
                  </div>
                  <div className="p-4">
                    <p className="text-center mb-4">
                      <span className="text-gray-600">Due Date - </span>
                      <span className="text-red-500 font-semibold">{getDueDate('GSTR3B')}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handlePrepareOnline('GSTR3B')}
                        className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm"
                      >
                        PREPARE ONLINE
                      </Button>
                      <Button variant="outline" className="flex-1 border-[#1e3a5f] text-[#1e3a5f] text-sm">
                        PREPARE OFFLINE
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* GSTR-2A Card */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded shadow-sm overflow-hidden">
                  <div className="bg-[#1e3a5f] text-white p-4 text-center">
                    <h3 className="font-semibold">Auto Drafted details (For view only)</h3>
                    <p className="text-lg font-bold mt-1">GSTR2A</p>
                  </div>
                  <div className="p-4">
                    <p className="text-center mb-4 text-gray-400">View Only</p>
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm">
                        VIEW
                      </Button>
                      <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2a5298] text-white text-sm">
                        DOWNLOAD
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a5298] text-white py-3 px-6 mt-auto">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs">
          <span>© 2025-26 Goods and Services Tax Network (Demo)</span>
          <span>Site Last Updated on {currentDate.toLocaleDateString('en-GB')}</span>
          <span>Designed & Developed by PractiQ</span>
        </div>
      </footer>
    </div>
  );
};

export default FileReturnsPage;
